import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useFirestore } from '../../contexts/FirestoreContext';
import { useAuth } from '../../contexts/AuthContext';
import { useMilestones } from '../../contexts/MilestoneDataContext';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, serverTimestamp, Timestamp, doc, getDoc, getDocs } from 'firebase/firestore';
import { Button, Modal, Form, Input, DatePicker, Select, message, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import MilestoneModal from './MilestoneModal';
import { ROLES, ROLE_PERMISSIONS } from '../../models/role';
import { createIncidentLog, getUserFullName, ACTIVITY_TYPES } from '../../utils/activityLogger';
import moment from 'moment';

// VISX imports
import { Group } from '@visx/group';
import { scaleTime, scaleLinear } from '@visx/scale';
import { AxisBottom } from '@visx/axis';
import { Zoom } from '@visx/zoom';
import { localPoint } from '@visx/event';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';

// D3 imports for time formatting and intervals
import { timeFormat } from 'd3-time-format';
import { timeDay, timeHour, timeMinute, timeWeek, timeMonth } from 'd3-time';

import './Timeline.css';
import { 
  updateRegulatoryDeadlineTimes
} from '../../utils/regulatoryDeadlines';
import { getUserDisplayName } from '../../utils/userDisplayUtils';
import { toDate, isOverdue as isDateOverdue, formatDate } from '../../utils/dateUtils';

const { Option } = Select;

// Timeline configuration
const TIMELINE_CONFIG = {
  margin: { top: 20, right: 20, bottom: 60, left: 160 }, // Reduced left from 200 to 160, reduced right from 80 to 20
  rowHeight: 62,
  rowPadding: 10,
  itemRadius: 8,
  itemSize: 16,
  minZoom: 0.1,
  maxZoom: 10,
  snapThreshold: 15, // pixels
};

// Timeline rows configuration
const TIMELINE_ROWS = [
  { id: 'incident', label: 'Incident Events', color: '#ff4d4f', bgColor: '#fff1f0' },
  { id: 'regulatory', label: 'Regulatory Deadlines', color: '#fa8c16', bgColor: '#fff7e6' },
  { id: 'milestones', label: 'Milestones', color: '#1890ff', bgColor: '#e6f7ff' },
  { id: 'tasks', label: 'Tasks', color: '#52c41a', bgColor: '#f6ffed' }
];

// Zoom levels with dynamic axis formatting
const ZOOM_LEVELS = [
  { 
    name: '1 Year', 
    duration: 365 * 24 * 60 * 60 * 1000, 
    tickInterval: timeMonth,
    tickFormat: timeFormat('%b %Y'),
    minorTickInterval: timeWeek,
    minorTickFormat: timeFormat('%d')
  },
  { 
    name: '6 Months', 
    duration: 180 * 24 * 60 * 60 * 1000, 
    tickInterval: timeMonth,
    tickFormat: timeFormat('%b %Y'),
    minorTickInterval: timeWeek,
    minorTickFormat: timeFormat('%d')
  },
  { 
    name: '3 Months', 
    duration: 90 * 24 * 60 * 60 * 1000, 
    tickInterval: timeWeek,
    tickFormat: timeFormat('%d %b'),
    minorTickInterval: timeDay,
    minorTickFormat: timeFormat('%d')
  },
  { 
    name: '1 Month', 
    duration: 30 * 24 * 60 * 60 * 1000, 
    tickInterval: timeWeek,
    tickFormat: timeFormat('%d %b'),
    minorTickInterval: timeDay,
    minorTickFormat: timeFormat('%d')
  },
  { 
    name: '2 Weeks', 
    duration: 14 * 24 * 60 * 60 * 1000, 
    tickInterval: timeDay,
    tickFormat: timeFormat('%d %b'),
    minorTickInterval: timeHour.every(6),
    minorTickFormat: timeFormat('%H:%M')
  },
  { 
    name: '1 Week', 
    duration: 7 * 24 * 60 * 60 * 1000, 
    tickInterval: timeDay,
    tickFormat: timeFormat('%d %b'),
    minorTickInterval: timeHour.every(6),
    minorTickFormat: timeFormat('%H:%M')
  },
  { 
    name: '3 Days', 
    duration: 3 * 24 * 60 * 60 * 1000, 
    tickInterval: timeHour.every(6),
    tickFormat: timeFormat('%H:%M %d %b'),
    minorTickInterval: timeHour,
    minorTickFormat: timeFormat('%H:%M')
  },
  { 
    name: '1 Day', 
    duration: 24 * 60 * 60 * 1000, 
    tickInterval: timeHour.every(3),
    tickFormat: timeFormat('%H:%M %d %b'),
    minorTickInterval: timeHour,
    minorTickFormat: timeFormat('%H:%M')
  },
  { 
    name: '12 Hours', 
    duration: 12 * 60 * 60 * 1000, 
    tickInterval: timeHour,
    tickFormat: timeFormat('%H:%M %d %b'),
    minorTickInterval: timeMinute.every(15),
    minorTickFormat: timeFormat('%H:%M')
  },
  { 
    name: '6 Hours', 
    duration: 6 * 60 * 60 * 1000, 
    tickInterval: timeMinute.every(30),
    tickFormat: timeFormat('%H:%M %d %b'),
    minorTickInterval: timeMinute.every(15),
    minorTickFormat: timeFormat('%H:%M')
  },
  { 
    name: '3 Hours', 
    duration: 3 * 60 * 60 * 1000, 
    tickInterval: timeMinute.every(15),
    tickFormat: timeFormat('%H:%M %d %b'),
    minorTickInterval: timeMinute.every(5),
    minorTickFormat: timeFormat('%H:%M')
  },
  { 
    name: '1 Hour', 
    duration: 60 * 60 * 1000, 
    tickInterval: timeMinute.every(5),
    tickFormat: timeFormat('%H:%M %d %b'),
    minorTickInterval: timeMinute,
    minorTickFormat: timeFormat('%H:%M')
  }
];

const DEFAULT_ZOOM_LEVEL = 8; // 12 Hours

const VISXTimeline = ({ incidentId, incident: incidentProp }) => {
  const { db } = useFirestore();
  const { currentUser } = useAuth();
  
  // State management
  const [tasks, setTasks] = useState([]);
  
  // Use shared milestone data
  const { milestones } = useMilestones({ 
    incidentId, 
    enableRealTime: true 
  });
  const [regulatoryDeadlines, setRegulatoryDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [userOrgId, setUserOrgId] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM_LEVEL);
  
  // Role options for task assignment
  const roleOptions = [
    { value: ROLES.CLIENT_SERVICE, label: ROLE_PERMISSIONS[ROLES.CLIENT_SERVICE].name },
    { value: ROLES.CMT_ADMIN, label: ROLE_PERMISSIONS[ROLES.CMT_ADMIN].name },
    { value: ROLES.CMT_LEADER, label: ROLE_PERMISSIONS[ROLES.CMT_LEADER].name },
    { value: ROLES.CMT_LEADER_ALT, label: ROLE_PERMISSIONS[ROLES.CMT_LEADER_ALT].name },
    { value: ROLES.COMMS_MEDIA, label: ROLE_PERMISSIONS[ROLES.COMMS_MEDIA].name },
    { value: ROLES.FACILITIES, label: ROLE_PERMISSIONS[ROLES.FACILITIES].name },
    { value: ROLES.FINANCE, label: ROLE_PERMISSIONS[ROLES.FINANCE].name },
    { value: ROLES.IT_TECH, label: ROLE_PERMISSIONS[ROLES.IT_TECH].name },
    { value: ROLES.LEGAL, label: ROLE_PERMISSIONS[ROLES.LEGAL].name },
    { value: ROLES.OTHER, label: ROLE_PERMISSIONS[ROLES.OTHER].name },
    { value: ROLES.SAFETY_SECURITY, label: ROLE_PERMISSIONS[ROLES.SAFETY_SECURITY].name },
    { value: ROLES.TALENT_HR, label: ROLE_PERMISSIONS[ROLES.TALENT_HR].name }
  ];
  
  // Modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
  const [isEditMilestoneModalVisible, setIsEditMilestoneModalVisible] = useState(false);
  const [isEditTaskModalVisible, setIsEditTaskModalVisible] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  
  // Forms for modals
  const [form] = Form.useForm();
  const [taskForm] = Form.useForm();
  const [editMilestoneForm] = Form.useForm();
  const [editTaskForm] = Form.useForm();
  
  // Pan and zoom state
  const [panOffset, setPanOffset] = useState(0); // Horizontal pan offset in milliseconds
  const [isPanning, setIsPanning] = useState(false);
  const [panStartX, setPanStartX] = useState(0);
  const [panStartOffset, setPanStartOffset] = useState(0);
  
  // Refs and drag state
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [containerWidth, setContainerWidth] = useState(1200);
  
  // Tooltip
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipLeft = 0,
    tooltipTop = 0,
  } = useTooltip();

  // Timeline dimensions - responsive to container
  const width = containerWidth;
  const height = TIMELINE_ROWS.length * TIMELINE_CONFIG.rowHeight + TIMELINE_CONFIG.margin.top + TIMELINE_CONFIG.margin.bottom;
  const innerWidth = width - TIMELINE_CONFIG.margin.left - TIMELINE_CONFIG.margin.right;
  const innerHeight = height - TIMELINE_CONFIG.margin.top - TIMELINE_CONFIG.margin.bottom;

  // Time range calculation based on zoom level, incident time, and pan offset
  const timeRange = useMemo(() => {
    const zoomConfig = ZOOM_LEVELS[zoomLevel];
    const centerTime = incidentProp?.occurredAt ? 
      (incidentProp.occurredAt.toDate ? incidentProp.occurredAt.toDate() : new Date(incidentProp.occurredAt)) :
      new Date();
    
    // Apply pan offset to the center time
    const pannedCenterTime = new Date(centerTime.getTime() + panOffset);
    
    const startTime = new Date(pannedCenterTime.getTime() - zoomConfig.duration / 2);
    const endTime = new Date(pannedCenterTime.getTime() + zoomConfig.duration / 2);
    
    return { startTime, endTime, centerTime: pannedCenterTime };
  }, [zoomLevel, incidentProp?.occurredAt, panOffset]);

  // Time scale
  const timeScale = useMemo(() => {
    return scaleTime({
      domain: [timeRange.startTime, timeRange.endTime],
      range: [0, innerWidth],
    });
  }, [timeRange, innerWidth]);

  // Row scale
  const rowScale = useMemo(() => {
    return scaleLinear({
      domain: [0, TIMELINE_ROWS.length],
      range: [0, innerHeight],
    });
  }, [innerHeight]);

  // Helper functions - using standardized date utility for legacy format compatibility

  // Get row index for timeline item type - defined first to avoid temporal dead zone
  const getRowIndex = useCallback((type) => {
    switch (type) {
      case 'incident': return 0;
      case 'regulatory': return 1;
      case 'regulatory_notification': return 1; // ADDED: Regulatory notification tasks go in regulatory row
      case 'milestone': return 2;  // Handle both 'milestone' and 'milestones'
      case 'milestones': return 2;
      case 'task': return 3;       // Handle both 'task' and 'tasks'
      case 'tasks': return 3;
      default: return 0;
    }
  }, []);

  // Generate dependency lines between tasks
  const generateDependencyLines = useCallback((items) => {
    const dependencyLines = [];
    
    // Find all tasks with prerequisites
    const tasksWithPrereqs = items.filter(item => 
      item.type === 'task' && 
      item.data?.prerequisites && 
      item.data.prerequisites.length > 0
    );
    
    tasksWithPrereqs.forEach(dependentTask => {
      const dependentX = timeScale(dependentTask.date);
      const dependentY = rowScale(getRowIndex(dependentTask.type)) + TIMELINE_CONFIG.rowHeight / 2;
      
      dependentTask.data.prerequisites.forEach(prereqId => {
        // Find the prerequisite task in the timeline items
        const prereqTask = items.find(item => 
          item.type === 'task' && item.data?.id === prereqId
        );
        
        if (prereqTask) {
          const prereqX = timeScale(prereqTask.date);
          const prereqY = rowScale(getRowIndex(prereqTask.type)) + TIMELINE_CONFIG.rowHeight / 2;
          
          // Only draw line if both tasks are visible in current time range
          if (prereqTask.date >= timeRange.startTime && 
              prereqTask.date <= timeRange.endTime &&
              dependentTask.date >= timeRange.startTime && 
              dependentTask.date <= timeRange.endTime) {
            
            dependencyLines.push({
              id: `dep-${prereqTask.data.id}-${dependentTask.data.id}`,
              prereqTask,
              dependentTask,
              prereqX,
              prereqY,
              dependentX,
              dependentY,
              isBlocked: prereqTask.status !== 'complete' && dependentTask.status !== 'complete',
              isOverdue: prereqTask.status !== 'complete' && isDateOverdue(dependentTask.data.dueDate)
            });
          }
        }
      });
    });
    
    return dependencyLines;
  }, [timeScale, rowScale, getRowIndex, timeRange, isDateOverdue]);

  const getItemIcon = useCallback((item) => {
    switch (item.type) {
      case 'incident': return '🚨';
      case 'regulatory': 
        if (item.status === 'completed') return '✅';
        if (item.status === 'in_progress') return '🔄';
        return '⚖️';
      case 'regulatory_notification': 
        if (item.status === 'complete') return '✅';
        if (item.status === 'in_progress') return '🔄';
        if (item.status === 'blocked') return '🚫';
        return '📋';
      case 'milestone': 
        if (item.status === 'completed') return '✅';
        if (item.status === 'in_progress') return '🔄';
        return '🎯';
      case 'task':
        if (item.status === 'complete') return '✅';
        if (item.status === 'in_progress') return '🔄';
        if (item.status === 'blocked') return '🚫';
        return '📋';
      default: return '●';
    }
  }, []);

  const getItemColor = useCallback((item) => {
    // Use urgency color for regulatory deadlines if available
    if (item.type === 'regulatory' && item.urgencyColor) {
      return item.urgencyColor;
    }
    
    // Use status-based colors for regulatory deadlines
    if (item.type === 'regulatory') {
      switch (item.status) {
        case 'completed': return '#52c41a'; // Green
        case 'overdue': return '#ff4d4f'; // Red
        case 'notified': return '#faad14'; // Orange
        default: 
          // Use urgency level colors
          switch (item.urgencyLevel) {
            case 'immediate': return '#ff0000'; // Bright red
            case 'critical': return '#fa541c'; // Orange-red
            case 'urgent': return '#faad14'; // Orange
            case 'warning': return '#fadb14'; // Yellow
            default: return '#fa8c16'; // Default regulatory orange
          }
      }
    }
    
    // Default row colors for other item types
    const row = TIMELINE_ROWS[getRowIndex(item.type)];
    return row?.color || '#666';
  }, [getRowIndex]);

  // Label priority function - higher numbers = higher priority (more likely to be shown)
  const getLabelPriority = useCallback((item) => {
    switch (item.type) {
      case 'incident':
        return 10; // Highest priority - always show incident
      case 'regulatory':
        return 8; // High priority - regulatory deadlines are important
      case 'milestone':
        return 6; // Medium priority
      case 'task':
        return 4; // Lower priority
      default:
        return 1;
    }
  }, []);

  // Smart grouping function for regulatory deadlines
  const groupRegulatoryDeadlines = useCallback((deadlines) => {

    
    const groups = [];
    const processed = new Set();
    
    deadlines.forEach((deadline, index) => {
      if (processed.has(index)) return;
      
      const deadlineDate = toDate(deadline.deadlineTime || deadline.dueDate);
      if (!deadlineDate || deadline.deleted) return;
      
      // Find other deadlines within grouping thresholds
      const exactMatches = []; // Within 30 minutes (identical timeframes)
      const closeMatches = []; // Within 6 hours (close proximity)
      
      deadlines.forEach((otherDeadline, otherIndex) => {
        if (otherIndex === index || processed.has(otherIndex)) return;
        
        const otherDate = toDate(otherDeadline.deadlineTime || otherDeadline.dueDate);
        if (!otherDate || otherDeadline.deleted) return;
        
        const timeDiff = Math.abs(deadlineDate.getTime() - otherDate.getTime());
        const minutesDiff = timeDiff / (1000 * 60);
        const hoursDiff = timeDiff / (1000 * 60 * 60);
        
        if (minutesDiff <= 30) {
          exactMatches.push({ deadline: otherDeadline, index: otherIndex });
        } else if (hoursDiff <= 6) {
          closeMatches.push({ deadline: otherDeadline, index: otherIndex });
        }
      });
      
      // Mark all matched deadlines as processed
      processed.add(index);
      exactMatches.forEach(match => processed.add(match.index));
      closeMatches.forEach(match => processed.add(match.index));
      
      // Create group based on matches
      const allDeadlines = [deadline, ...exactMatches.map(m => m.deadline), ...closeMatches.map(m => m.deadline)];
      
      if (exactMatches.length > 0) {
        // Scenario 1: Identical timeframes - Vertical stacking
        groups.push({
          type: 'stacked',
          date: deadlineDate,
          deadlines: [deadline, ...exactMatches.map(m => m.deadline)],
          count: exactMatches.length + 1
        });
      } else if (closeMatches.length > 0) {
        // Scenario 2: Close proximity - Combined icon
        groups.push({
          type: 'combined',
          date: deadlineDate,
          deadlines: allDeadlines,
          count: allDeadlines.length
        });
      } else {
        // Individual deadline
        groups.push({
          type: 'individual',
          date: deadlineDate,
          deadlines: [deadline],
          count: 1
        });
      }
    });
    

    
    return groups;
  }, [toDate]);

  // Process timeline items
  const timelineItems = useMemo(() => {
    const items = [];
    
    // Add incident occurrence if available
    if (incidentProp?.occurredAt) {
      const incidentDate = toDate(incidentProp.occurredAt);
      if (incidentDate) {
        items.push({
          id: 'incident-occurrence',
          type: 'incident',
          date: incidentDate,
          label: 'INCIDENT OCCURRED',
          draggable: true,
          data: incidentProp
        });
      }
    }

    // Add regulatory deadlines with smart grouping
    
    const regulatoryGroups = groupRegulatoryDeadlines(regulatoryDeadlines);
    

    
    regulatoryGroups.forEach((group, groupIndex) => {
      if (group.type === 'stacked') {
        // Vertical stacking for identical timeframes - CREATE SINGLE ITEM FOR ENTIRE STACK
        const primaryDeadline = group.deadlines[0];
        const regulatorNames = group.deadlines.map(d => d.regulator).join(', ');
        const label = `${group.count} Stacked Deadlines`;
        
        items.push({
          id: `regulatory-stack-${groupIndex}`,
          type: 'regulatory',
          subtype: 'stacked',
          date: group.date,
          label,
          draggable: false,
          count: group.count,
          groupId: `group-${groupIndex}`,
          deadlines: group.deadlines,
          status: primaryDeadline.status,
          urgencyLevel: primaryDeadline.urgencyLevel,
          urgencyColor: primaryDeadline.urgencyColor,
          data: {
            ...primaryDeadline,
            regulatorNames,
            allDeadlines: group.deadlines
          }
        });
      } else if (group.type === 'combined') {
        // Combined icon for close proximity
        const primaryDeadline = group.deadlines[0];
        const regulatorNames = group.deadlines.map(d => d.regulator).join(', ');
        const label = `${group.count} Regulatory Deadlines`;
        
        items.push({
          id: `regulatory-group-${groupIndex}`,
          type: 'regulatory',
          subtype: 'combined',
          date: group.date,
          label,
          draggable: false,
          count: group.count,
          groupId: `group-${groupIndex}`,
          deadlines: group.deadlines,
          status: primaryDeadline.status,
          urgencyLevel: primaryDeadline.urgencyLevel,
          urgencyColor: primaryDeadline.urgencyColor,
          data: {
            ...primaryDeadline,
            regulatorNames,
            allDeadlines: group.deadlines
          }
        });
      } else {
        // Individual deadline
        const deadline = group.deadlines[0];
        const label = `${deadline.regulator} - ${deadline.trigger}`;
        
        items.push({
          id: `regulatory-${deadline.id}`,
          type: 'regulatory',
          subtype: 'individual',
          date: group.date,
          label,
          draggable: false,
          status: deadline.status,
          urgencyLevel: deadline.urgencyLevel,
          urgencyColor: deadline.urgencyColor,
          data: deadline
        });
      }
    });

    // Add milestones
    milestones.forEach(milestone => {
      const milestoneDate = toDate(milestone.dueDate);
      if (milestoneDate) {
        items.push({
          id: `milestone-${milestone.id}`,
          type: 'milestone',
          date: milestoneDate,
          label: milestone.title,
          status: milestone.status,
          draggable: true,
          data: milestone
        });
      }
    });

    // Add tasks
    tasks.forEach(task => {
      const taskDate = toDate(task.dueDate);
      if (taskDate) {
        // Determine if this is a regulatory notification task
        const isRegulatoryTask = task.type === 'regulatory_notification';
        
        items.push({
          id: `task-${task.id}`,
          type: isRegulatoryTask ? 'regulatory_notification' : 'task', // Use specific type for regulatory tasks
          date: taskDate,
          label: task.title || task.name || 'Unnamed Task',
          status: task.status,
          draggable: true,
          data: task,
          // Add regulatory-specific styling
          urgencyLevel: isRegulatoryTask ? (task.isUrgent ? 'critical' : 'normal') : undefined,
          urgencyColor: isRegulatoryTask ? (task.isUrgent ? '#ff4d4f' : '#1890ff') : undefined
        });
      }
    });

    const filteredItems = items.filter(item => 
      item.date >= timeRange.startTime && item.date <= timeRange.endTime
    );
    
    return filteredItems;
  }, [milestones, tasks, regulatoryDeadlines, incidentProp, timeRange, toDate]);

  // Calculate dependency lines for visual indicators
  const dependencyLines = useMemo(() => {
    return generateDependencyLines(timelineItems);
  }, [timelineItems, generateDependencyLines]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    if (zoomLevel < ZOOM_LEVELS.length - 1) {
      setZoomLevel(prev => prev + 1);
    }
  }, [zoomLevel]);

  const handleZoomOut = useCallback(() => {
    if (zoomLevel > 0) {
      setZoomLevel(prev => prev - 1);
    }
  }, [zoomLevel]);

  const handleSnapToNow = useCallback(() => {
    const now = new Date();
    const incidentTime = incidentProp?.occurredAt ? 
      (incidentProp.occurredAt.toDate ? incidentProp.occurredAt.toDate() : new Date(incidentProp.occurredAt)) :
      new Date();
    
    // Calculate the offset needed to center on current time
    const offsetToNow = now.getTime() - incidentTime.getTime();
    setPanOffset(offsetToNow);
    
    console.log('Snapping to current time:', now);
  }, [incidentProp?.occurredAt]);

  // Pan handlers
  const handleTimelinePanStart = useCallback((event) => {
    // Only start panning if not clicking on an item
    if (event.target.tagName === 'circle' || event.target.tagName === 'text') {
      console.log('🚫 Not starting pan - clicked on item');
      return;
    }
    
    // Get mouse position relative to the SVG
    const svgRect = svgRef.current.getBoundingClientRect();
    const mouseX = event.clientX - svgRect.left - TIMELINE_CONFIG.margin.left;

    setIsPanning(true);
    setPanStartX(mouseX);
    setPanStartOffset(panOffset);
    event.preventDefault();
    
    console.log('🤏 Pan started');
  }, [panOffset]);

  const handleTimelinePanMove = useCallback((event) => {
    if (!isPanning) return;

    // Get mouse position relative to the SVG
    const svgRect = svgRef.current.getBoundingClientRect();
    const mouseX = event.clientX - svgRect.left - TIMELINE_CONFIG.margin.left;

    const deltaX = mouseX - panStartX;
    const deltaTime = (deltaX / innerWidth) * (timeRange.endTime.getTime() - timeRange.startTime.getTime());
    setPanOffset(panStartOffset - deltaTime);
    event.preventDefault();
  }, [isPanning, panStartX, panStartOffset, innerWidth, timeRange]);

  const handleTimelinePanEnd = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Mouse wheel zoom handler
  const handleWheel = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const delta = event.deltaY;
    const isZoomIn = delta < 0;
    
    if (isZoomIn && zoomLevel < ZOOM_LEVELS.length - 1) {
      setZoomLevel(prev => prev + 1);
    } else if (!isZoomIn && zoomLevel > 0) {
      setZoomLevel(prev => prev - 1);
    }
  }, [zoomLevel]);

  // Item drag handlers
  const handleMouseDown = useCallback((event, item) => {

    
    if (!item.draggable) {

      return;
    }
    
    event.preventDefault();
    event.stopPropagation(); // Prevent timeline panning when dragging items
    
    // Get mouse position relative to the SVG
    const svgRect = svgRef.current.getBoundingClientRect();
    const mouseX = event.clientX - svgRect.left - TIMELINE_CONFIG.margin.left;
    const mouseY = event.clientY - svgRect.top - TIMELINE_CONFIG.margin.top;
    


    setDraggedItem(item);
    setIsDragging(true);
    setDragOffset({
      x: mouseX - timeScale(item.date),
      y: 0
    });
    

  }, [timeScale]);

  const handleMouseMove = useCallback((event) => {
    // Handle timeline panning
    if (isPanning) {
      handleTimelinePanMove(event);
      return;
    }

    // Handle item dragging
    if (!isDragging || !draggedItem) return;

    event.preventDefault();
    
    // Get mouse position relative to the SVG
    const svgRect = svgRef.current.getBoundingClientRect();
    const mouseX = event.clientX - svgRect.left - TIMELINE_CONFIG.margin.left;
    
    const newX = mouseX - dragOffset.x;
    const newDate = timeScale.invert(newX);
    
    console.log('🔄 Dragging:', { mouseX, newX, newDate: newDate.toISOString() });
    
    // Update the dragged item's position (temporary visual update)
    setDraggedItem(prev => ({ ...prev, date: newDate }));
  }, [isDragging, draggedItem, dragOffset, timeScale, isPanning, handleTimelinePanMove]);

  // Drag handler functions (simplified versions)
  const handleIncidentOccurrenceDrag = async (newTime) => {
    if (!incidentProp?.id) return;
    
    try {
      const incidentRef = doc(db, 'c3_incidents', incidentProp.id);
      const newTimestamp = Timestamp.fromDate(newTime);
      
      await updateDoc(incidentRef, {
        occurredAt: newTimestamp,
        updatedAt: Timestamp.now()
      });
      
      // Update regulatory deadlines if we have the required data
      if (userOrgId && currentUser?.uid) {
        console.log('🔄 Updating regulatory deadlines after incident time change');
        
        // Get organization regulatory rules for recalculation
        try {
          const { doc: docImport, getDoc } = await import('firebase/firestore');
          const orgRef = docImport(db, 'c3_organisations', userOrgId);
          const orgDoc = await getDoc(orgRef);
          
          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            const regulatoryRules = orgData.regulatoryRules || [];
            
            if (regulatoryRules.length > 0) {
              // Update regulatory deadlines
              await updateRegulatoryDeadlineTimes(
                incidentProp.id,
                userOrgId,
                newTime,
                regulatoryRules, // Use organization rules, not existing deadlines
                currentUser.uid,
                db
              );

              
              // ADDED: Also update regulatory notification task due dates
              const { updateRegulatoryTaskTimes } = await import('../../utils/regulatoryDeadlines');
              await updateRegulatoryTaskTimes(
                incidentProp.id,
                userOrgId,
                newTime,
                regulatoryRules,
                currentUser.uid,
                db
              );

            } else {

            }
          } else {

          }
        } catch (regulatoryError) {
          console.error('❌ Timeline drag: Failed to update regulatory deadlines:', regulatoryError);
        }
      } else {
        console.log('ℹ️ Skipping regulatory deadline update:', {
          hasUserOrgId: !!userOrgId,
          hasCurrentUser: !!currentUser?.uid
        });
      }
      
      // Trigger form update by dispatching a custom event
      // This will notify the parent component to refresh the form fields
      window.dispatchEvent(new CustomEvent('incidentTimelineUpdate', {
        detail: {
          incidentId: incidentProp.id,
          occurredAt: newTimestamp,
          newTime: newTime
        }
      }));
      
      message.success('Incident occurrence time updated');
    } catch (error) {
      console.error('Error updating incident occurrence:', error);
      message.error('Failed to update incident occurrence');
    }
  };

  const handleMilestoneDrag = async (milestoneId, newTime) => {
    try {
      const milestoneRef = doc(db, 'c3_milestones', milestoneId);
      await updateDoc(milestoneRef, {
        dueDate: Timestamp.fromDate(newTime)
      });
      message.success('Milestone updated');
    } catch (error) {
      console.error('Error updating milestone:', error);
      message.error('Failed to update milestone');
    }
  };

  const handleTaskDrag = async (taskId, newTime) => {
    try {
      const taskRef = doc(db, 'c3_tasks', taskId);
      await updateDoc(taskRef, {
        dueDate: Timestamp.fromDate(newTime),
        updatedAt: Timestamp.now(),
        updateReason: 'timeline_drag'
      });
      message.success('Task updated');
    } catch (error) {
      console.error('Error updating task:', error);
      message.error('Failed to update task');
    }
  };

  const handleRegulatoryTaskDrag = async (taskId, newTime) => {
    try {
      const taskRef = doc(db, 'c3_tasks', taskId);
      await updateDoc(taskRef, {
        dueDate: Timestamp.fromDate(newTime),
        updatedAt: Timestamp.now(),
        updateReason: 'regulatory_timeline_drag'
      });
      message.success('Regulatory notification task updated');
    } catch (error) {
      console.error('Error updating regulatory task:', error);
      message.error('Failed to update regulatory task');
    }
  };

  const handleRegulatoryDeadlineDrag = async (deadlineId, newTime) => {
    try {
      const deadlineRef = doc(db, 'c3_regulatory_deadlines', deadlineId);
      await updateDoc(deadlineRef, {
        deadlineTime: Timestamp.fromDate(newTime),
        updatedAt: Timestamp.now()
      });
      message.success('Regulatory deadline updated');
    } catch (error) {
      console.error('Error updating regulatory deadline:', error);
      message.error('Failed to update regulatory deadline');
    }
  };

  const handleMouseUp = useCallback(async (event) => {
    // End timeline panning
    if (isPanning) {
      handleTimelinePanEnd();
      return;
    }

    // Handle item drag end
    if (!isDragging || !draggedItem) return;

    event.preventDefault();
    
    // Get mouse position relative to the SVG
    const svgRect = svgRef.current.getBoundingClientRect();
    const mouseX = event.clientX - svgRect.left - TIMELINE_CONFIG.margin.left;
    
    const newX = mouseX - dragOffset.x;
    const newDate = timeScale.invert(newX);

    console.log('🏁 Drag ended:', { 
      itemType: draggedItem.type, 
      newDate: newDate.toISOString(),
      originalDate: draggedItem.data?.occurredAt || draggedItem.date
    });

    // Update in database based on item type
    try {
      if (draggedItem.type === 'incident') {
        console.log('🚨 Updating incident occurrence time...');
        await handleIncidentOccurrenceDrag(newDate);
      } else if (draggedItem.type === 'milestone') {
        await handleMilestoneDrag(draggedItem.data.id, newDate);
      } else if (draggedItem.type === 'task') {
        await handleTaskDrag(draggedItem.data.id, newDate);
      } else if (draggedItem.type === 'regulatory_notification') {
        await handleRegulatoryTaskDrag(draggedItem.data.id, newDate);
      } else if (draggedItem.type === 'regulatory') {
        await handleRegulatoryDeadlineDrag(draggedItem.data.id, newDate);
      }
    } catch (error) {
      console.error('Error updating item:', error);
      message.error('Failed to update item');
    }

    setDraggedItem(null);
    setIsDragging(false);
    setDragOffset({ x: 0, y: 0 });
  }, [isDragging, draggedItem, dragOffset, timeScale, isPanning, handleTimelinePanEnd, handleIncidentOccurrenceDrag, handleMilestoneDrag, handleTaskDrag, handleRegulatoryTaskDrag, handleRegulatoryDeadlineDrag]);

  // Item click handlers
  const handleItemClick = useCallback((item) => {
    if (item.type === 'milestone') {
      setEditingMilestone(item.data);
      setIsEditMilestoneModalVisible(true);
    } else if (item.type === 'task') {
      setEditingTask(item.data);
      setIsEditTaskModalVisible(true);
    }
  }, []);

  // Timeline export functionality
  const exportTimelineData = useCallback(async (format = 'csv') => {
    try {
      const exportData = {
        incident: {
          id: incidentProp?.id,
          title: incidentProp?.title,
          occurredAt: incidentProp?.occurredAt ? formatDate(incidentProp.occurredAt, 'YYYY-MM-DD HH:mm:ss') : 'Not set',
          exportedAt: new Date().toISOString()
        },
        timeline: timelineItems.map(item => ({
          type: item.type,
          id: item.id,
          label: item.label,
          date: formatDate(item.date, 'YYYY-MM-DD HH:mm:ss'),
          status: item.status,
          // Add type-specific data
          ...(item.type === 'task' && {
            assignedRole: item.data?.assignedRole,
            owner: item.data?.owner,
            prerequisites: item.data?.prerequisites?.length || 0,
            priority: item.data?.priority
          }),
          ...(item.type === 'milestone' && {
            description: item.data?.description,
            status: item.data?.status
          }),
          ...(item.type === 'regulatory' && {
            regulator: item.data?.regulator,
            trigger: item.data?.trigger,
            urgencyLevel: item.data?.urgencyLevel,
            method: Array.isArray(item.data?.method) ? item.data.method.join(', ') : item.data?.method
          })
        })),
        dependencies: dependencyLines.map(line => ({
          prerequisiteTask: line.prereqTask.label,
          dependentTask: line.dependentTask.label,
          isBlocked: line.isBlocked,
          isOverdue: line.isOverdue,
          status: line.isOverdue ? 'Overdue' : line.isBlocked ? 'Blocked' : 'Ready'
        })),
        summary: {
          totalItems: timelineItems.length,
          byType: {
            tasks: timelineItems.filter(i => i.type === 'task').length,
            milestones: timelineItems.filter(i => i.type === 'milestone').length,
            regulatory: timelineItems.filter(i => i.type === 'regulatory').length
          },
          dependencies: dependencyLines.length,
          blockedTasks: dependencyLines.filter(d => d.isBlocked).length,
          overdueTasks: dependencyLines.filter(d => d.isOverdue).length
        }
      };

      if (format === 'json') {
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `crisis-timeline-${incidentProp?.id || 'export'}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        // CSV Export
        const csvRows = [];
        
        // Headers
        csvRows.push(['Type', 'Label', 'Date', 'Status', 'Additional Info'].join(','));
        
        // Timeline items
        exportData.timeline.forEach(item => {
          const additionalInfo = [];
          if (item.assignedRole) additionalInfo.push(`Role: ${item.assignedRole}`);
          if (item.owner) additionalInfo.push(`Owner: ${item.owner}`);
          if (item.prerequisites) additionalInfo.push(`Prerequisites: ${item.prerequisites}`);
          if (item.regulator) additionalInfo.push(`Regulator: ${item.regulator}`);
          if (item.trigger) additionalInfo.push(`Trigger: ${item.trigger}`);
          
          csvRows.push([
            item.type,
            `"${item.label}"`,
            item.date,
            item.status || 'N/A',
            `"${additionalInfo.join('; ')}"`
          ].join(','));
        });
        
        // Dependencies section
        if (exportData.dependencies.length > 0) {
          csvRows.push(''); // Empty row
          csvRows.push(['DEPENDENCIES'].join(','));
          csvRows.push(['Prerequisite Task', 'Dependent Task', 'Status', 'Notes'].join(','));
          
          exportData.dependencies.forEach(dep => {
            const notes = [];
            if (dep.isOverdue) notes.push('OVERDUE');
            if (dep.isBlocked) notes.push('BLOCKED');
            
            csvRows.push([
              `"${dep.prerequisiteTask}"`,
              `"${dep.dependentTask}"`,
              dep.status,
              `"${notes.join(', ')}"`
            ].join(','));
          });
        }
        
        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `crisis-timeline-${incidentProp?.id || 'export'}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      
      message.success(`Timeline exported successfully as ${format.toUpperCase()}`);
      
      // Log export activity
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        await createIncidentLog({
          incidentId: incidentProp?.id,
          orgId: userOrgId,
          action: 'TIMELINE_EXPORTED',
          details: `Timeline exported in ${format.toUpperCase()} format with ${exportData.timeline.length} items`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority: 'normal',
          metadata: {
            exportFormat: format,
            itemCount: exportData.timeline.length,
            dependencyCount: exportData.dependencies.length
          }
        });
      } catch (logError) {
        console.error('Failed to log timeline export:', logError);
      }
      
    } catch (error) {
      console.error('Error exporting timeline:', error);
      message.error('Failed to export timeline');
    }
  }, [timelineItems, dependencyLines, incidentProp, userOrgId, currentUser, db, formatDate]);

  // Save handlers for new items
  const handleMilestoneSave = useCallback(async (milestoneData) => {
    try {
      const milestonesRef = collection(db, 'c3_milestones');
      const docRef = await addDoc(milestonesRef, {
        ...milestoneData,
        incidentId,
        orgId: userOrgId,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid
      });
      
      setIsModalVisible(false);
      form.resetFields();
      message.success('Milestone created successfully');
      
      // Log milestone creation
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        await createIncidentLog({
          incidentId,
          orgId: userOrgId,
          action: ACTIVITY_TYPES.MILESTONE_CREATED,
          details: `Milestone created: "${milestoneData.title}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority: 'normal'
        });
      } catch (logError) {
        console.error('Failed to log milestone creation:', logError);
      }
    } catch (error) {
      console.error('Error creating milestone:', error);
      message.error('Failed to create milestone');
    }
  }, [db, incidentId, userOrgId, currentUser, form]);

  const handleTaskSave = useCallback(async (values) => {
    if (!userOrgId) {
      message.error('Organisation ID not found');
      return;
    }

    try {
      const tasksRef = collection(db, 'c3_tasks');
      
      // Filter out undefined values to prevent Firestore errors
      const filteredValues = Object.fromEntries(
        Object.entries(values).filter(([_, value]) => value !== undefined)
      );
      
      // Fix field name consistency - use both 'name' and 'title' for compatibility
      const taskData = {
        ...filteredValues,
        name: values.name || values.title,
        title: values.title || values.name, // Ensure both fields exist during transition
        incidentId,
        orgId: userOrgId,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        status: 'not_started',
        prerequisites: [],
        dueDate: values.dueDate ? Timestamp.fromDate(new Date(values.dueDate)) : null
      };
      
      const docRef = await addDoc(tasksRef, taskData);
      setIsTaskModalVisible(false);
      taskForm.resetFields();
      message.success('Task created successfully');
      
      // Log task creation
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        await createIncidentLog({
          incidentId,
          orgId: userOrgId,
          action: ACTIVITY_TYPES.TASK_CREATED,
          details: `Task created: "${values.name || values.title}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority: 'normal',
          metadata: {
            taskId: docRef.id,
            taskName: values.name || values.title,
            assignedRole: values.assignedRole,
            owner: values.owner,
            dueDate: values.dueDate
          }
        });
      } catch (logError) {
        console.error('Failed to log task creation:', logError);
      }
    } catch (error) {
      console.error('Error creating task:', error);
      message.error('Failed to create task');
    }
  }, [db, incidentId, userOrgId, currentUser, taskForm]);

  // Effect to handle container resize
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        const newWidth = Math.max(width, 800); // Minimum width of 800px
        console.log('📏 Container resized:', { oldWidth: containerWidth, newWidth, contentWidth: width });
        setContainerWidth(newWidth);
      }
    });

    resizeObserver.observe(containerRef.current);

    // Set initial width
    const initialWidth = containerRef.current.offsetWidth;
    const newWidth = Math.max(initialWidth, 800);
    console.log('📏 Initial container width:', { initialWidth, newWidth });
    setContainerWidth(newWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Fetch data effects (simplified - you'll need to implement the full data fetching logic)
  useEffect(() => {
    if (!incidentId || !currentUser || !userOrgId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Milestones are now handled by MilestoneDataContext

        // Fetch tasks
        const tasksQuery = query(
          collection(db, 'c3_tasks'),
          where('incidentId', '==', incidentId),
          where('orgId', '==', userOrgId),
          where('deleted', '==', false), // Exclude soft-deleted tasks
          orderBy('createdAt', 'desc')
        );
        
        const unsubscribeTasks = onSnapshot(tasksQuery, (snapshot) => {
          const taskList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setTasks(taskList);
        });

        // Fetch regulatory deadlines using universal function

        
        const regulatoryQuery = query(
          collection(db, 'c3_regulatory_deadlines'),
          where('incidentId', '==', incidentId),
          where('orgId', '==', userOrgId),
          where('deleted', '==', false), // Explicitly exclude deleted records
          orderBy('deadlineTime', 'asc')
        );
        
        const unsubscribeRegulatory = onSnapshot(regulatoryQuery, (snapshot) => {
          const regulatoryList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            // Ensure dates are properly converted
            deadlineTime: doc.data().deadlineTime?.toDate(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate()
          }));
          

          
          setRegulatoryDeadlines(regulatoryList);
        });

        setDataLoaded(true);
        setLoading(false);

        // Note: Automatic task creation removed per user request
        // Only regulatory deadlines are managed automatically

        return () => {
          unsubscribeTasks();
          unsubscribeRegulatory();
        };
      } catch (error) {
        console.error('Error fetching timeline data:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [incidentId, currentUser, userOrgId, db]);

  // Get user org ID - using correct collection name
  useEffect(() => {
    const fetchUserOrgId = async () => {
      if (!currentUser) return;
      
      try {
        const userDocRef = doc(db, 'c3_users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const orgId = userDoc.data().orgId;
          setUserOrgId(orgId);
        }
      } catch (error) {
        console.error('Error fetching user org ID:', error);
      }
    };

    fetchUserOrgId();
  }, [currentUser, db]);

  // VISXTimeline is a DISPLAY-ONLY component - regulatory deadlines are created by Timeline.jsx

  // VISXTimeline is DISPLAY-ONLY - regulatory deadline management happens in RegulatoryComplianceTab

  if (loading) {
    return <div className="timeline-wrapper">Loading timeline...</div>;
  }

  if (error) {
    return <div className="timeline-wrapper">Error: {error}</div>;
  }

  return (
    <div className="timeline-wrapper" ref={containerRef} style={{ width: '100%' }}>
      {/* Controls */}
      <div className="timeline-controls">
        <div className="timeline-controls-left">
          <Tooltip title="Create a new milestone to track key goals and checkpoints in your crisis response.">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
              style={{ backgroundColor: '#39325b', borderColor: '#39325b', marginRight: '8px' }}
            >
              Add Milestone
            </Button>
          </Tooltip>
          <Tooltip title="Create a new task with specific assignments and deadlines.">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsTaskModalVisible(true)}
              style={{ backgroundColor: '#39325b', borderColor: '#39325b', marginRight: '8px' }}
            >
              Add Task
            </Button>
          </Tooltip>
        </div>
        <div className="timeline-controls-right">
          <Tooltip title="Current timeline zoom level">
            <div className="zoom-level-display" style={{ marginRight: '12px', fontSize: '14px', color: '#666' }}>
              {ZOOM_LEVELS[zoomLevel].name}
            </div>
          </Tooltip>
          <Tooltip title="Zoom in to see more detail">
            <Button
              type="primary"
              onClick={handleZoomIn}
              disabled={zoomLevel >= ZOOM_LEVELS.length - 1}
              style={{ backgroundColor: '#39325b', borderColor: '#39325b', marginRight: '8px' }}
            >
              Zoom In
            </Button>
          </Tooltip>
          <Tooltip title="Zoom out to see broader view">
            <Button
              type="primary"
              onClick={handleZoomOut}
              disabled={zoomLevel <= 0}
              style={{ backgroundColor: '#39325b', borderColor: '#39325b', marginRight: '8px' }}
            >
              Zoom Out
            </Button>
          </Tooltip>
          <Tooltip title="Center timeline on current time">
            <Button
              type="primary"
              onClick={handleSnapToNow}
              style={{ backgroundColor: '#39325b', borderColor: '#39325b', marginRight: '8px' }}
            >
              Snap to Now
            </Button>
          </Tooltip>
          <Tooltip title="Export timeline data as CSV for documentation and compliance">
            <Button
              type="default"
              onClick={() => exportTimelineData('csv')}
              style={{ marginRight: '8px' }}
            >
              Export CSV
            </Button>
          </Tooltip>
          <Tooltip title="Export timeline data as JSON for detailed analysis">
            <Button
              type="default"
              onClick={() => exportTimelineData('json')}
            >
              Export JSON
            </Button>
          </Tooltip>
        </div>
      </div>

      {/* Timeline SVG */}
      <div style={{ position: 'relative', userSelect: 'none', width: '100%', overflow: 'hidden' }}>
        <svg
          ref={svgRef}
          width={width}
          height={height}
          style={{ 
            border: '1px solid #e8e8e8', 
            borderRadius: '4px', 
            background: '#fff',
            cursor: isPanning ? 'grabbing' : (isDragging ? 'grabbing' : 'grab'),
            width: '100%',
            maxWidth: `${width}px`
          }}
          onMouseDown={(e) => {
            console.log('🖱️ SVG mousedown:', e.target.tagName);
            handleTimelinePanStart(e);
          }}
          onMouseMove={(e) => {
            handleMouseMove(e);
          }}
          onMouseUp={(e) => {
            console.log('🖱️ SVG mouseup');
            handleMouseUp(e);
          }}
          onMouseLeave={(e) => {
            console.log('🖱️ SVG mouseleave');
            handleMouseUp(e);
          }}
          onWheel={handleWheel}
        >
          <Group left={TIMELINE_CONFIG.margin.left} top={TIMELINE_CONFIG.margin.top}>
            {/* Timeline border */}
            <rect
              x={0}
              y={0}
              width={innerWidth}
              height={innerHeight}
              fill="none"
              stroke="#d9d9d9"
              strokeWidth={1}
            />
            
            {/* Row backgrounds */}
            {TIMELINE_ROWS.map((row, index) => (
              <rect
                key={row.id}
                x={0}
                y={rowScale(index)}
                width={innerWidth}
                height={TIMELINE_CONFIG.rowHeight}
                fill={row.bgColor}
                stroke="#e8e8e8"
                strokeWidth={0.5}
              />
            ))}

            {/* Vertical grid lines aligned with axis ticks */}
            {(() => {
              const gridLines = [];
              const zoomConfig = ZOOM_LEVELS[zoomLevel];
              
              // Use the same tick generation logic as the axis
              const ticks = timeScale.ticks(zoomConfig.tickInterval);
              
              ticks.forEach((tick, index) => {
                const x = timeScale(tick);
                
                if (x >= 0 && x <= innerWidth) {
                  gridLines.push(
                    <line
                      key={`grid-${tick.getTime()}`}
                      x1={x}
                      y1={0}
                      x2={x}
                      y2={innerHeight}
                      stroke="#e0e0e0"
                      strokeWidth={1}
                      opacity={0.6}
                    />
                  );
                }
              });
              
              // Add left and right edge lines for clear boundaries
              gridLines.push(
                <line
                  key="left-edge"
                  x1={0}
                  y1={0}
                  x2={0}
                  y2={innerHeight}
                  stroke="#999"
                  strokeWidth={2}
                />
              );
              
              gridLines.push(
                <line
                  key="right-edge"
                  x1={innerWidth}
                  y1={0}
                  x2={innerWidth}
                  y2={innerHeight}
                  stroke="#999"
                  strokeWidth={2}
                />
              );
              
              return gridLines;
            })()}

            {/* Row labels with matching background colors */}
            {TIMELINE_ROWS.map((row, index) => (
              <g key={`label-${row.id}`}>
                {/* Label background - matches row background color */}
                <rect
                  x={-TIMELINE_CONFIG.margin.left}
                  y={rowScale(index)}
                  width={TIMELINE_CONFIG.margin.left - 10}
                  height={TIMELINE_CONFIG.rowHeight}
                  fill={row.bgColor}
                  stroke="#e8e8e8"
                  strokeWidth={0.5}
                />
                {/* Label text */}
                <text
                  x={-15}
                  y={rowScale(index) + TIMELINE_CONFIG.rowHeight / 2}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={13}
                  fontWeight="600"
                  fill="#333"
                  style={{ userSelect: 'none' }}
                >
                  {row.label}
                </text>
              </g>
            ))}

            {/* Dependency lines between tasks */}
            {dependencyLines.map(line => {
              // Calculate control points for curved line
              const deltaX = line.dependentX - line.prereqX;
              const deltaY = line.dependentY - line.prereqY;
              const controlOffset = Math.min(Math.abs(deltaX) * 0.5, 50);
              
              // Determine line style based on dependency status
              const lineColor = line.isOverdue ? '#ff4d4f' : // Red for overdue
                               line.isBlocked ? '#faad14' : // Orange for blocked
                               '#52c41a'; // Green for ready/complete
              
              const lineWidth = line.isOverdue ? 3 : line.isBlocked ? 2 : 1.5;
              const dashArray = line.isBlocked ? '5,5' : 'none';
              
              return (
                <g key={line.id} className="dependency-line">
                  {/* Main dependency line */}
                  <path
                    d={`M ${line.prereqX + TIMELINE_CONFIG.itemRadius} ${line.prereqY} 
                        C ${line.prereqX + controlOffset} ${line.prereqY} 
                          ${line.dependentX - controlOffset} ${line.dependentY} 
                          ${line.dependentX - TIMELINE_CONFIG.itemRadius} ${line.dependentY}`}
                    stroke={lineColor}
                    strokeWidth={lineWidth}
                    strokeDasharray={dashArray}
                    fill="none"
                    opacity={0.7}
                    style={{ pointerEvents: 'none' }}
                  />
                  
                  {/* Arrow head */}
                  <polygon
                    points={`${line.dependentX - TIMELINE_CONFIG.itemRadius - 6},${line.dependentY - 3} 
                             ${line.dependentX - TIMELINE_CONFIG.itemRadius},${line.dependentY} 
                             ${line.dependentX - TIMELINE_CONFIG.itemRadius - 6},${line.dependentY + 3}`}
                    fill={lineColor}
                    opacity={0.8}
                    style={{ pointerEvents: 'none' }}
                  />
                  
                  {/* Dependency status indicator */}
                  {line.isBlocked && (
                    <circle
                      cx={line.prereqX + (line.dependentX - line.prereqX) / 2}
                      cy={line.prereqY + (line.dependentY - line.prereqY) / 2}
                      r={4}
                      fill="#faad14"
                      stroke="#fff"
                      strokeWidth={1}
                      opacity={0.9}
                    >
                      <title>Task blocked by incomplete prerequisite</title>
                    </circle>
                  )}
                  
                  {line.isOverdue && (
                    <circle
                      cx={line.prereqX + (line.dependentX - line.prereqX) / 2}
                      cy={line.prereqY + (line.dependentY - line.prereqY) / 2}
                      r={4}
                      fill="#ff4d4f"
                      stroke="#fff"
                      strokeWidth={1}
                      opacity={0.9}
                    >
                      <title>Overdue task with incomplete prerequisite</title>
                    </circle>
                  )}
                </g>
              );
            })}

            {/* Timeline items with smart collision detection */}
            {(() => {
              // Group items by row for collision detection
              const itemsByRow = {};
              timelineItems.forEach(item => {
                const rowIndex = getRowIndex(item.type);
                if (!itemsByRow[rowIndex]) itemsByRow[rowIndex] = [];
                itemsByRow[rowIndex].push(item);
              });

              // Sort items in each row by x position
              Object.keys(itemsByRow).forEach(rowIndex => {
                itemsByRow[rowIndex].sort((a, b) => timeScale(a.date) - timeScale(b.date));
              });

              // Calculate which labels should be hidden due to overlaps
              const hiddenLabels = new Set();
              
              Object.keys(itemsByRow).forEach(rowIndex => {
                const rowItems = itemsByRow[rowIndex];
                
                for (let i = 0; i < rowItems.length; i++) {
                  const currentItem = rowItems[i];
                  const currentX = timeScale(currentItem.date);
                  const currentLabelStart = currentX + TIMELINE_CONFIG.itemRadius + 8;
                  const currentLabelWidth = Math.min(currentItem.label.length * 6.5, 150); // More accurate text width estimation
                  const currentLabelEnd = currentLabelStart + currentLabelWidth;
                  
                  // Check for overlaps with subsequent items
                  for (let j = i + 1; j < rowItems.length; j++) {
                    const nextItem = rowItems[j];
                    const nextX = timeScale(nextItem.date);
                    const nextIconStart = nextX - TIMELINE_CONFIG.itemRadius;
                    
                    // If current label overlaps with next item's icon or label area
                    if (currentLabelEnd > nextIconStart - 5) { // 5px buffer
                      // Prioritize which label to hide based on importance
                      const currentPriority = getLabelPriority(currentItem);
                      const nextPriority = getLabelPriority(nextItem);
                      
                      if (currentPriority <= nextPriority) {
                        hiddenLabels.add(currentItem.id);
                      } else {
                        hiddenLabels.add(nextItem.id);
                      }
                    }
                  }
                }
              });

              return timelineItems.map((item, itemIndex) => {
                const x = timeScale(item.date);
                const rowIndex = getRowIndex(item.type);
                const baseY = rowScale(rowIndex) + TIMELINE_CONFIG.rowHeight / 2;
                const color = getItemColor(item);
                const icon = getItemIcon(item);
                
                // Use dragged position if this item is being dragged
                const displayX = isDragging && draggedItem?.id === item.id ? timeScale(draggedItem.date) : x;
                
                // Determine if this label should be hidden
                const shouldHideLabel = hiddenLabels.has(item.id);
                
                // Handle different regulatory subtypes
                if (item.type === 'regulatory') {
                  if (item.subtype === 'stacked') {
                    // Vertical stacking for identical timeframes - render multiple icons as one group
                    const y = baseY;
                    
                    // Calculate label positioning
                    let labelX = displayX + TIMELINE_CONFIG.itemRadius + 8;
                    let labelY = y;
                    let textAnchor = 'start';
                    
                    const labelText = item.label.length > 20 ? `${item.label.substring(0, 20)}...` : item.label;
                    const estimatedTextWidth = labelText.length * 6.5;
                    
                    if (textAnchor === 'start' && labelX + estimatedTextWidth > innerWidth) {
                      labelX = displayX - TIMELINE_CONFIG.itemRadius - 8;
                      textAnchor = 'end';
                    }

                    return (
                      <g key={item.id}>
                        {/* Render multiple stacked circles */}
                        {item.deadlines.map((deadline, stackIndex) => {
                          const stackOffset = (stackIndex - (item.count - 1) / 2) * 18; // 18px spacing
                          const stackY = y + stackOffset;
                          
                          return (
                            <g key={`stack-${stackIndex}`}>
                              <circle
                                cx={displayX}
                                cy={stackY}
                                r={TIMELINE_CONFIG.itemRadius}
                                fill={color}
                                stroke="#fff"
                                strokeWidth={2}
                                style={{ cursor: 'pointer' }}
                              />
                              <text
                                x={displayX}
                                y={stackY}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize={10}
                                style={{ pointerEvents: 'none', userSelect: 'none' }}
                              >
                                {icon}
                              </text>
                            </g>
                          );
                        })}
                        
                        {/* Single tooltip area covering all stacked items */}
                        <rect
                          x={displayX - TIMELINE_CONFIG.itemRadius - 5}
                          y={y - (item.count * 9) - 5}
                          width={(TIMELINE_CONFIG.itemRadius + 5) * 2}
                          height={(item.count * 18) + 10}
                          fill="transparent"
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => {
                            const tooltipX = displayX + TIMELINE_CONFIG.margin.left;
                            const tooltipY = y + TIMELINE_CONFIG.margin.top - 10;
                            
                            const regulatorList = item.deadlines.map(d => 
                              `${d.regulator} - ${d.trigger}`
                            ).join(', ');
                            
                            showTooltip({
                              tooltipData: {
                                item,
                                icon,
                                stackInfo: `${item.count} stacked deadlines: ${regulatorList}`
                              },
                              tooltipLeft: tooltipX,
                              tooltipTop: tooltipY,
                            });
                          }}
                          onMouseLeave={hideTooltip}
                        />
                        
                        {/* Stacked group label */}
                        {!shouldHideLabel && (
                          <text
                            x={labelX}
                            y={labelY}
                            textAnchor={textAnchor}
                            dominantBaseline="middle"
                            fontSize={11}
                            fill="#333"
                            style={{ pointerEvents: 'none', userSelect: 'none', fontWeight: 'bold' }}
                          >
                            {labelText}
                          </text>
                        )}
                      </g>
                    );
                  } else if (item.subtype === 'combined') {
                    // Combined icon for close proximity
                    const y = baseY;
                    
                    // Calculate label positioning
                    let labelX = displayX + TIMELINE_CONFIG.itemRadius + 8;
                    let labelY = y;
                    let textAnchor = 'start';
                    
                    const labelText = item.label.length > 20 ? `${item.label.substring(0, 20)}...` : item.label;
                    const estimatedTextWidth = labelText.length * 6.5;
                    
                    if (textAnchor === 'start' && labelX + estimatedTextWidth > innerWidth) {
                      labelX = displayX - TIMELINE_CONFIG.itemRadius - 8;
                      textAnchor = 'end';
                    }

                    return (
                      <g key={item.id}>
                        {/* Combined item marker */}
                        <circle
                          cx={displayX}
                          cy={y}
                          r={TIMELINE_CONFIG.itemRadius + 2} // Slightly larger for combined
                          fill={color}
                          stroke="#fff"
                          strokeWidth={2}
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => {
                            const tooltipX = displayX + TIMELINE_CONFIG.margin.left;
                            const tooltipY = y + TIMELINE_CONFIG.margin.top - 10;
                            
                            const regulatorList = item.deadlines.map(d => 
                              `${d.regulator} (${d.urgencyLevel})`
                            ).join(', ');
                            
                            showTooltip({
                              tooltipData: {
                                item,
                                icon,
                                combinedInfo: `${item.count} deadlines: ${regulatorList}`
                              },
                              tooltipLeft: tooltipX,
                              tooltipTop: tooltipY,
                            });
                          }}
                          onMouseLeave={hideTooltip}
                        />
                        
                        {/* Combined item icon */}
                        <text
                          x={displayX}
                          y={y}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize={10}
                          style={{ pointerEvents: 'none', userSelect: 'none' }}
                        >
                          {icon}
                        </text>
                        
                        {/* Count indicator */}
                        <text
                          x={displayX + TIMELINE_CONFIG.itemRadius + 2}
                          y={y - TIMELINE_CONFIG.itemRadius - 2}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize={8}
                          fill="#fff"
                          style={{ pointerEvents: 'none', userSelect: 'none', fontWeight: 'bold' }}
                        >
                          {item.count}
                        </text>
                        
                        {/* Combined item label */}
                        {!shouldHideLabel && (
                          <text
                            x={labelX}
                            y={labelY}
                            textAnchor={textAnchor}
                            dominantBaseline="middle"
                            fontSize={11}
                            fill="#333"
                            style={{ pointerEvents: 'none', userSelect: 'none', fontWeight: 'bold' }}
                          >
                            {labelText}
                          </text>
                        )}
                      </g>
                    );
                  }
                }
                
                // Default rendering for individual items and non-regulatory items
                const y = baseY;
                
                // Calculate label positioning
                let labelX = displayX + TIMELINE_CONFIG.itemRadius + 8;
                let labelY = y;
                let textAnchor = 'start';
                
                const labelText = item.label.length > 20 ? `${item.label.substring(0, 20)}...` : item.label;
                const estimatedTextWidth = labelText.length * 6.5;
                
                if (textAnchor === 'start' && labelX + estimatedTextWidth > innerWidth) {
                  labelX = displayX - TIMELINE_CONFIG.itemRadius - 8;
                  textAnchor = 'end';
                }

                return (
                  <g key={item.id}>
                    {/* Item marker */}
                    <circle
                      cx={displayX}
                      cy={y}
                      r={TIMELINE_CONFIG.itemRadius}
                      fill={color}
                      stroke="#fff"
                      strokeWidth={2}
                      style={{ 
                        cursor: isDragging && draggedItem?.id === item.id ? 'grabbing' : (item.draggable ? 'grab' : 'pointer'),
                        filter: isDragging && draggedItem?.id === item.id ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : 'none',
                        transition: isDragging && draggedItem?.id === item.id ? 'none' : 'all 0.2s ease'
                      }}
                      onMouseDown={(e) => {
                        console.log('🖱️ Circle mousedown:', item.type, item.draggable);
                        handleMouseDown(e, item);
                      }}
                      onClick={(e) => {
                        // Only trigger click if not dragging
                        if (!isDragging) {
                          handleItemClick(item);
                        }
                      }}
                      onMouseEnter={(e) => {
                        const tooltipX = displayX + TIMELINE_CONFIG.margin.left;
                        const tooltipY = y + TIMELINE_CONFIG.margin.top - 10;
                        
                        showTooltip({
                          tooltipData: {
                            item,
                            icon,
                          },
                          tooltipLeft: tooltipX,
                          tooltipTop: tooltipY,
                        });
                      }}
                      onMouseLeave={hideTooltip}
                    />
                    
                    {/* Item icon (emoji) */}
                    <text
                      x={displayX}
                      y={y}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize={10}
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {icon}
                    </text>
                    
                    {/* Item label - only show if not hidden due to collision */}
                    {!shouldHideLabel && (
                      <text
                        x={labelX}
                        y={labelY}
                        textAnchor={textAnchor}
                        dominantBaseline="middle"
                        fontSize={11}
                        fill="#333"
                        style={{ pointerEvents: 'none', userSelect: 'none', fontWeight: 'bold' }}
                      >
                        {labelText}
                      </text>
                    )}
                    
                    {/* Visual indicator for hidden labels */}
                    {shouldHideLabel && (
                      <circle
                        cx={displayX + TIMELINE_CONFIG.itemRadius + 4}
                        cy={y - TIMELINE_CONFIG.itemRadius - 4}
                        r={2}
                        fill="#666"
                        opacity={0.6}
                      />
                    )}
                  </g>
                );
              });
            })()}

            {/* Current time indicator */}
            {(() => {
              const now = new Date();
              if (now >= timeRange.startTime && now <= timeRange.endTime) {
                const x = timeScale(now);
                return (
                  <line
                    x1={x}
                    y1={0}
                    x2={x}
                    y2={innerHeight}
                    stroke="#ff4d4f"
                    strokeWidth={2}
                    strokeDasharray="4,4"
                    opacity={0.7}
                  />
                );
              }
              return null;
            })()}

            {/* X-axis */}
            <AxisBottom
              scale={timeScale}
              top={innerHeight}
              tickFormat={ZOOM_LEVELS[zoomLevel].tickFormat}
              tickValues={timeScale.ticks(ZOOM_LEVELS[zoomLevel].tickInterval)}
              tickLabelProps={() => ({
                fill: '#666',
                fontSize: 11,
                textAnchor: 'middle',
                fontWeight: 'bold',
              })}
              strokeWidth={0}
            />
            
            {/* Axis line to ensure it extends full width */}
            <line
              x1={0}
              y1={innerHeight}
              x2={innerWidth}
              y2={innerHeight}
              stroke="#333"
              strokeWidth={1}
            />
          </Group>
        </svg>

        {/* Tooltip */}
        {tooltipData && (
          <TooltipWithBounds left={tooltipLeft} top={tooltipTop}>
            <div style={{ padding: '8px', background: '#333', color: '#fff', borderRadius: '4px', fontSize: '12px', maxWidth: '300px' }}>
              <div><strong>{tooltipData.icon} {tooltipData.item.label}</strong></div>
              <div>{moment(tooltipData.item.date).format('HH:mm DD MMM')}</div>
              {tooltipData.item.status && <div>Status: {tooltipData.item.status}</div>}
              {tooltipData.stackInfo && (
                <div style={{ marginTop: '4px', fontSize: '11px', opacity: 0.9 }}>
                  📚 {tooltipData.stackInfo}
                </div>
              )}
              {tooltipData.combinedInfo && (
                <div style={{ marginTop: '4px', fontSize: '11px', opacity: 0.9 }}>
                  🔗 {tooltipData.combinedInfo}
                </div>
              )}
              {tooltipData.item.draggable && <div style={{ opacity: 0.8 }}>Drag to reschedule</div>}
            </div>
          </TooltipWithBounds>
        )}
      </div>

      {/* Timeline Legend */}
      <div className="timeline-legend">
        <div className="legend-title">Timeline Legend:</div>
        <div className="legend-items">
          {TIMELINE_ROWS.map((row) => (
            <Tooltip 
              key={row.id}
              title={`${row.label}: ${row.id === 'incident' ? 'Marks when the crisis occurred' : 
                     row.id === 'regulatory' ? 'Smart-grouped compliance deadlines (stacked for identical times, combined for close proximity)' :
                     row.id === 'milestones' ? 'Key goals and checkpoints' : 
                     'Actionable items with assignments'}`}
              placement="top"
            >
              <div className="legend-item">
                <span className="legend-icon" style={{ color: row.color }}>
                  {row.id === 'incident' ? '🚨' : 
                   row.id === 'regulatory' ? '⚖️' :
                   row.id === 'milestones' ? '🎯' : '📋'}
                </span>
                <span className="legend-label">{row.label}</span>
                <span className={`legend-draggable ${row.id === 'regulatory' ? 'auto-calculated' : 'draggable'}`}>
                  {row.id === 'regulatory' ? 'Auto-calculated' : 'Draggable'}
                </span>
              </div>
            </Tooltip>
          ))}
        </div>
      </div>

      {/* Add Milestone Modal */}
      <MilestoneModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onSave={handleMilestoneSave}
        incidentId={incidentId}
      />

      {/* Add Task Modal */}
      <Modal
        title="New Task"
        open={isTaskModalVisible}
        onCancel={() => setIsTaskModalVisible(false)}
        footer={null}
      >
        <Form
          form={taskForm}
          layout="vertical"
          onFinish={handleTaskSave}
        >
          <Form.Item
            name="name"
            label={<span style={{ fontWeight: 'bold' }}>Task Name</span>}
            rules={[{ required: true, message: 'Please enter task name' }]}
          >
            <Input placeholder="Enter task name" />
          </Form.Item>

          <Form.Item
            name="description"
            label={<span style={{ fontWeight: 'bold' }}>Description</span>}
          >
            <Input.TextArea rows={4} placeholder="Enter task description" />
          </Form.Item>

          <Form.Item
            name="assignedRole"
            label={<span style={{ fontWeight: 'bold' }}>Assigned Role</span>}
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select placeholder="Select assigned role">
              {roleOptions.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="owner"
            label={<span style={{ fontWeight: 'bold' }}>Owner</span>}
            rules={[{ 
              required: !(editingTask?.autoCreated && editingTask?.type === 'regulatory_notification'), 
              message: 'Please enter owner name' 
            }]}
          >
            <Input placeholder={
              editingTask?.autoCreated && editingTask?.type === 'regulatory_notification' 
                ? 'Unassigned (Role-based task)' 
                : 'Enter task owner name'
            } />
          </Form.Item>

          <Form.Item
            name="dueDate"
            label={<span style={{ fontWeight: 'bold' }}>Due Date</span>}
          >
            <DatePicker 
              style={{ width: '100%' }}
              showTime
              format="YYYY-MM-DD HH:mm"
              placeholder="Select due date and time"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
            >
              Create Task
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VISXTimeline;